import { getCompanyCode, getEffectiveCompanyCode } from '../context/CompanyContext';

const companyCode = () => (getEffectiveCompanyCode() || 'default').toLowerCase();
const authKey = () => `authData:${companyCode()}`;
const activityKey = () => `lastActivity:${companyCode()}`;

const parse = (value) => {
  try { return value ? JSON.parse(value) : null; } catch { return null; }
};

const tokenCompany = (data) => {
  const token = data?.admin?.Token || data?.token;
  if (!token) return '';
  try {
    const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.company_code || '').toLowerCase();
  } catch {
    return '';
  }
};

const tokenIsUsable = (data) => {
  const token = data?.admin?.Token || data?.token;
  if (!token) return false;
  try {
    const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expected = companyCode();
    const actual = String(payload.company_code || '').toLowerCase();
    if (payload.exp && payload.exp * 1000 <= Date.now()) return false;
    if (expected !== 'default' && actual !== expected) return false;
    return true;
  } catch {
    return false;
  }
};

// These endpoints establish a session and must never receive a cached session.
export const isPublicAuthRequest = (url = '') => {
  try {
    const pathname = new URL(String(url), window.location.origin).pathname.toLowerCase();
    return (
      /\/(?:admin\/)?login\/?$/.test(pathname) ||
      /\/api\/public\//.test(pathname) ||
      /\/employee-form\/[a-z0-9_-]+\/?$/i.test(pathname) ||
      /\/api\/onboarding\/(?:sign|upload)\/[a-z0-9_-]+(?:\/pages|\/submit)?\/?$/i.test(pathname) ||
      /\/api\/(admin\/reset-password|send-otp|verify-otp|company-modules)\/?$/.test(pathname)
    );
  } catch {
    return false;
  }
};

export const readAuthData = () => {
  const scoped = parse(localStorage.getItem(authKey()));
  if (scoped) {
    if (tokenIsUsable(scoped)) return scoped;
    localStorage.removeItem(authKey());
    localStorage.removeItem(activityKey());
  }

  // Migrate an existing single-company session only when its JWT belongs to
  // this URL's tenant. Never reuse another company's localhost session.
  const legacy = parse(localStorage.getItem('authData'));
  const expected = companyCode();
  const actual = tokenCompany(legacy);
  if (legacy && tokenIsUsable(legacy) && (expected === 'default' || actual === expected)) {
    localStorage.setItem(authKey(), JSON.stringify(legacy));
    return legacy;
  }
  return null;
};

export const writeAuthData = (data) => {
  const serialized = JSON.stringify(data);
  localStorage.setItem(authKey(), serialized);
  // Keep the legacy key for components not yet migrated; API clients always
  // use the tenant-scoped value above.
  localStorage.setItem('authData', serialized);
};

export const clearAuthData = () => {
  localStorage.removeItem(authKey());
  localStorage.removeItem(activityKey());
  const legacy = parse(localStorage.getItem('authData'));
  if (!legacy || tokenCompany(legacy) === companyCode()) {
    localStorage.removeItem('authData');
    localStorage.removeItem('lastActivity');
  }
};

export const handleUnauthorized = (requestUrl = '') => {
  if (isPublicAuthRequest(requestUrl)) return;
  const wasAdmin = Boolean(readAuthData()?.admin);
  clearAuthData();
  const code = getCompanyCode();
  const base = code ? `/${code}` : '';
  const loginPath = `${base}${wasAdmin ? '/admin' : '/login'}`;
  if (window.location.pathname !== loginPath) window.location.assign(loginPath);
};

export const readLastActivity = () => (
  parseInt(localStorage.getItem(activityKey()) || '0', 10)
);

export const writeLastActivity = (value = Date.now()) => {
  localStorage.setItem(activityKey(), String(value));
};
